import { Arrow, Content, Portal, Provider, Root, Trigger } from '@radix-ui/react-tooltip';
import { forwardRef, type ReactNode } from 'react';
import { cn } from '../../utils/';

export type TooltipProps = {
  delayDuration?: number;
  children: ReactNode;
  text?: ReactNode;
  sideOffset?: number;
  align?: 'start' | 'center' | 'end';
  className?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  isArrowVisible?: boolean;
  arrowClassName?: string;
  textClassName?: string;
  isDisabled?: boolean;
  contentChildren?: ReactNode;
  container?: HTMLDivElement | null;
  /**
   * Render the trigger onto the child instead of wrapping it in a button. Use when the child is
   * already a control (button, link, popover trigger); it must be a single element that forwards refs.
   */
  asChild?: boolean;
  triggerClassName?: string;
  /** Names the trigger button, e.g. "About Linear buffer". The tooltip text is its description, not its name. */
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'data-testid'?: string;
};

export const Tooltip = forwardRef<HTMLButtonElement, TooltipProps>(
  (
    {
      delayDuration,
      children,
      text,
      sideOffset,
      align,
      className,
      side,
      isArrowVisible,
      arrowClassName,
      textClassName,
      isDisabled,
      contentChildren,
      container,
      asChild = false,
      triggerClassName,
      ...props
    },
    ref,
  ) => {
    return (
      <Provider delayDuration={delayDuration} skipDelayDuration={0} disableHoverableContent={isDisabled}>
        <Root>
          <Trigger asChild ref={ref} {...props}>
            {asChild ? (
              children
            ) : (
              <button
                type="button"
                data-slot="tooltip-trigger"
                // The centred ::after keeps the pointer target at least 24×24 px around a small icon.
                className={cn(
                  'relative inline-flex cursor-pointer items-center rounded-sm focus-ring',
                  "after:absolute after:top-1/2 after:left-1/2 after:size-6 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']",
                  triggerClassName,
                )}
              >
                {children}
              </button>
            )}
          </Trigger>
          <Portal container={container}>
            {isDisabled ? null : (
              <Content
                className={cn(
                  // z-[150] clears the client's legacy Dialog stack (content up to z 140) and Sheet (z 100); pointer
                  // events stay on so the pointer can rest on the tooltip under a modal's `pointer-events: none` body.
                  'pointer-events-auto relative z-[150] select-none rounded-sm bg-grey-80 px-2 py-1 text-white flex items-center',
                  'shadow-md will-change-[transform,opacity]',
                  'data-[state=delayed-open]:data-[side=bottom]:animate-slideUpAndFade data-[state=delayed-open]:data-[side=left]:animate-slideRightAndFade data-[state=delayed-open]:data-[side=right]:animate-slideLeftAndFade data-[state=delayed-open]:data-[side=top]:animate-slideDownAndFade',
                  className,
                )}
                sideOffset={sideOffset}
                align={align}
                side={side}
              >
                {contentChildren ? (
                  contentChildren
                ) : (
                  <span className={cn('text-xs text-white', textClassName)}>{text}</span>
                )}
                {isArrowVisible && <Arrow className={cn('fill-grey-90', arrowClassName)} />}
              </Content>
            )}
          </Portal>
        </Root>
      </Provider>
    );
  },
);
